import {
    Box,
    Dialog,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography,
    Card,
    CardMedia,
    CardActionArea,
    Badge, useTheme
} from "@mui/material";
import React, {useState} from "react";
import {Coach} from "./Coach";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {styled} from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import themedBoxShadow from "../../styling/themedBoxShadow";
import ImportantText from "../ImportantText";

interface CoachComponentProps {
    coach: Coach;
}

const CoachComponent: React.FC<CoachComponentProps> = ({coach}) => {
    const [open, setOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    const theme = useTheme();

    const GalleryImage = styled('img')({
        maxWidth: '90vw',
        maxHeight: '85vh',
        objectFit: 'contain',
        '@media (max-width: 900px)': {
            width: '100%',
            maxWidth: '100%',
        },
    });

    const handleOpen = (index: number) => {
        setCurrentIndex(index);
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const handlePrev = () => {
        setCurrentIndex((prev) => (prev === 0 ? coach.imagesUrls.length - 1 : prev - 1));
    };

    const handleNext = () => {
        setCurrentIndex((prev) => (prev === coach.imagesUrls.length - 1 ? 0 : prev + 1));
    };

    return (
        <Stack direction={{xs: 'column', md: 'row'}} gap="20px" justifyContent="space-between" paddingTop="40px"
               paddingBottom="20px">
            <Stack direction="column" gap="10px">
                <Typography variant="h6">
                    {coach.name}
                </Typography>
                <List>
                    <ListItem key="100">
                        <ListItemIcon>
                            <CheckCircleOutlineIcon color="primary"/>
                        </ListItemIcon>
                        <ListItemText>
                            <ImportantText>{coach.passengerSeats}</ImportantText> de locuri pentru pasageri
                        </ListItemText>
                    </ListItem>
                    {coach.facilities.map((facility: string, index: number) =>
                        <ListItem key={index}>
                            <ListItemIcon>
                                <CheckCircleOutlineIcon color="primary"/>
                            </ListItemIcon>
                            <ListItemText primary={facility}/>
                        </ListItem>
                    )}
                </List>
            </Stack>
            <Box sx={{maxWidth: '490px'}}>
                <Badge
                    badgeContent={coach.imagesUrls.length}
                    sx={{
                        width: '100%',
                        height: '100%',
                        boxShadow: themedBoxShadow(theme),
                        '& .MuiBadge-badge': {
                            backgroundColor: "background.default",
                            right: 20,
                            top: 20,
                            fontSize: '16px',
                            padding: '6px 10px',
                            minWidth: '26px',
                            height: '26px',
                        }
                    }}
                >
                    <Card sx={{width: '100%', height: '100%', position: 'relative'}}>
                        <CardActionArea onClick={() => handleOpen(0)} sx={{width: '100%', height: '100%'}}>
                            <CardMedia
                                component="img"
                                image={coach.imagesUrls[0]}
                                alt="imagine autocar"
                                sx={{width: '100%', height: '100%', objectFit: 'cover'}}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 8,
                                    right: 8,
                                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                    borderRadius: 1,
                                    p: 0.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <PhotoLibraryIcon sx={{color: 'white', fontSize: 20}}/>
                            </Box>
                        </CardActionArea>
                    </Card>
                </Badge>
            </Box>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth={false}
                sx={{
                    '& .MuiDialog-container': {
                        '@media (max-width: 900px)': {
                            padding: 0,
                        },
                    },
                    '& .MuiDialog-paper': {
                        '@media (max-width: 900px)': {
                            margin: 0,
                            width: '100%',
                            maxWidth: '100%',
                        },
                    },
                }}
                PaperProps={{
                    sx: {
                        backgroundColor: 'primary.dark',
                        borderRadius: '20px',
                    },
                }}
            >
                <IconButton
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 18,
                        top: 18,
                        color: 'white',
                        zIndex: 1,
                    }}
                >
                    <CloseIcon/>
                </IconButton>
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        p: 2,
                        '@media (max-width: 900px)': {
                            p: 0,
                        },
                    }}
                >
                    <IconButton
                        onClick={handlePrev}
                        sx={{color: 'white', position: 'absolute', left: 16}}
                    >
                        <ChevronLeftIcon fontSize="large"/>
                    </IconButton>
                    <GalleryImage
                        src={coach.imagesUrls[currentIndex]}
                        alt={`imagine autocar ${currentIndex + 1}`}
                    />
                    <IconButton
                        onClick={handleNext}
                        sx={{color: 'white', position: 'absolute', right: 16}}
                    >
                        <ChevronRightIcon fontSize="large"/>
                    </IconButton>
                </Box>
            </Dialog>
        </Stack>
    )
}

export default CoachComponent;